/**
 * Platform detection utilities for native vs web.
 * Uses Capacitor's isNativePlatform() when available.
 */
import { Capacitor } from '@capacitor/core';

export function isNativeApp(): boolean {
  return Capacitor.isNativePlatform();
}

export function getPlatform(): 'ios' | 'android' | 'web' {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') return 'ios';
  if (platform === 'android') return 'android';
  return 'web';
}

/**
 * Open a URL in an in-app system browser (Chrome Custom Tab on Android,
 * SFSafariViewController on iOS) on native; opens a new browser tab on web.
 *
 * On native, `<a target="_blank">` is silently ignored by Capacitor's
 * WebView — the link would otherwise navigate inside the main WebView and
 * break the SPA's back-navigation state (white page on return). Always
 * use this helper for external / static HTML links (privacy, terms, etc).
 */
export async function openExternalUrl(url: string): Promise<void> {
  if (isNativeApp()) {
    try {
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url });
      return;
    } catch {
      // Plugin not yet registered in this APK — fall through to the
      // window.open path so the user still reaches the page.
    }
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}
